<template>
    <Modal v-if="shown"
           :title="$t('seller.payments.clearing.modal.title')"
           :close-on-click-outside="false"
           :width="800"
           @close="show(false)"
    >
        <template v-slot:body>
            <div class="modalContent">
                <div class="base_margin assignmentInfo">
                    <h4>{{$t("seller.payments.clearing.modal.assignmentInfo.title")}}</h4>
                    <md-field displayOnly>
                        <label>{{$t("common.invoiceNumber")}}</label>
                        <md-input class="withLink" :value="details.invoiceNo" readonly
                                  @click="showReceivableInfo(details.receivableId)"></md-input>
                    </md-field>
                    <md-field displayOnly>
                        <label>{{$t("common.investor")}}</label>
                        <md-input class="withLink" :value="details.buyer" readonly
                                  @click="showCompanyInfo(details.buyer, details.buyerId)"></md-input>
                    </md-field>
                    <md-field displayOnly>
                        <label>{{$t("common.debtor")}}</label>
                        <md-input class="withLink" :value="details.debtor" readonly
                                  @click="showCompanyInfo(details.debtor, details.debtorId)"></md-input>
                    </md-field>
                </div>
                <form class="base_margin paymentInfo">
                    <h4>{{$t("seller.payments.clearing.modal.paymentInfo.title")}}</h4>
					<md-field v-if="hasAccounts">
						<label>{{$t("seller.payments.clearing.modal.label.sourceAccount")}}</label>
						<TbSelect v-model="selectedSourceAccount"
								  :search-allow="true"
								  :options="details.accounts"
								  options-value-field="id"
								  :options-display-getter="(item) => item.tag || item.iban"
								  required
						/>
					</md-field>
					<md-field>
						<label>{{$t("common.amount")}}</label>
						<MoneyAmountInput v-model="clearingAmount" :currency="details.currency" required></MoneyAmountInput>
					</md-field>
					<md-field displayOnly>
						<label>{{$t("seller.payments.clearing.modal.label.iban")}}</label>
						<md-input :value="finalPaymentDetails.toIban" disabled></md-input>
					</md-field>
					<md-field displayOnly>
						<label>{{$t("seller.payments.clearing.modal.label.bic")}}</label>
						<md-input :value="finalPaymentDetails.toBic" disabled></md-input>
					</md-field>
					<md-field displayOnly>
						<label>{{$t("common.reasonOfTransfer")}}</label>
						<md-textarea :value="finalPaymentDetails.reason" :md-autogrow="true" disabled></md-textarea>
					</md-field>
					<div class="text_left light_bg_color ">
						<span class="font_bold">{{$t("common.hint")}}:</span>
						<br/>
						<span>{{$t("seller.payments.clearing.modal.clearingAmount.hint")}}</span>
					</div>
                </form>
            </div>
            <div class="flex flex_end base_margin">
                <md-button :class="{ 'md-primary': startPaymentStatus }"
                           :disabled="!startPaymentStatus"
                           @click="confirmStartPayment">
                    {{$t("seller.payments.clearing.modal.button.startPayment")}}
                </md-button>
            </div>
        </template>
    </Modal>
</template>

<script>
import Modal from "@/components/templates/modal/Modal";
import {sellerPaymentsApi} from "@/common/api/sellerPaymentsApi";
import MoneyAmountInput from "@/components/templates/common/MoneyAmountInput";
import {formatAsMoneyAmount} from "@/common/format";
import {sellerPaymentsInitiationApi} from "@/common/api/sellerPaymentsInitiationApi";

export default {
    components: {
        MoneyAmountInput,
        Modal
    },
    data() {
        return {
            shown: false,
            details: {},
            selectedSourceAccount: null,
            clearingAmount: null
        };
    },
    computed: {
        startPaymentStatus() {
            return this.clearingAmount && this.selectedSourceAccount;
        },
        hasAccounts() {
			return this.details.accounts && this.details.accounts.length > 0
        },
		selectedAccountUseS2B() {
			return this.details.accounts.find(account => account.id === this.selectedSourceAccount).useS2B;
		},
		s2bDetails() {
			return {
				settlementId: this.details.s2bRecordId,
				toIban: this.details.s2bToIban,
				toBic: this.details.s2bToBic,
				reason: this.details.s2bReason,
			};
		},
		s2tDetails() {
			return {
				settlementId: this.details.s2tRecordId,
				toIban: this.details.s2tToIban,
				toBic: this.details.s2tToBic,
				reason: this.details.s2tReason,
			};
		},
		finalPaymentDetails() {
			if (!this.selectedSourceAccount) {
				return {};
			}

			if (this.details.buyerAccountUseS2B && this.selectedAccountUseS2B) {
				return this.s2bDetails;
			} else {
				return this.s2tDetails;
			}
		}
    },
    methods: {
        showWithReceivableId(receivableId) {
            this.loadDetails(receivableId);
        },
        loadDetails(receivableId) {
            sellerPaymentsInitiationApi.inspection.getManualTransferDetails(receivableId)
                .then(res => {
                    this.details = res;
                    this.clearingAmount = res.minAmount;
                    this.selectedSourceAccount = null;

                    this.show(true);
                })
                .catch(this.toast);
        },
        confirmStartPayment() {
            let option = {
                msg: this.$t("seller.payments.clearing.modal.startPayment.confirm.msg", {
                    amount: formatAsMoneyAmount(this.clearingAmount, { currency: this.details.currency })
                }),
                confirmFun: this.startPayment
            };
            this.confirmMeg(option);
        },
        startPayment() {
            let clearingPayment = {
                settlementId: this.finalPaymentDetails.settlementId,
                fromAccountId: this.selectedSourceAccount,
                currency: this.details.currency,
                amount: this.clearingAmount
            };
            sellerPaymentsInitiationApi.operation.startClearingPayment(clearingPayment)
                .then(() => {
                    this.toastSuccess();
					this.$emit("payment");
                    this.show(false);
                })
                .catch(e => {
                    this.toast(e);
                });
        },
        show(value) {
            this.shown = value;
        },
        showReceivableInfo(receivableId) {
            this.$store.dispatch("setActionNoticModal", {
                show: true,
                data: {
                    title: this.$t("ReceDetail.Receivable.Details"),
                    type: "ReceivableDetailsModal",
                    width: 800,
                    data: { id: receivableId }
                }
            });
        },
        showCompanyInfo(companyName, companyId) {
            this.$store.dispatch("setActionNoticModal", {
                show: true,
                data: {
                    type: "CompanyDetailsModal",
					title: companyName,
                    width: 800,
                    data: { id: companyId }
                }
            });
        }
    },
};
</script>

<style lang="scss" scoped>
@import "@/assets/scss/base_variables.scss";

.modalContent {
    display: flex;

    .assignmentInfo {
        width: 50%;
    }

    .paymentInfo {
        width: 100%;
    }

    h4 {
        padding-bottom: 10px;
    }

    input.withLink {
        cursor: pointer;
        -webkit-text-fill-color: $accent_color !important;
    }
}

</style>